import { useEffect, useMemo, useRef, useState } from "react";
import { normalizePatientId } from "src-next/modules/clinical-insights/helpers/insightPractitionerUtils";
import { useInsightOrganizationPatients } from "src-next/modules/clinical-insights/hooks/useInsightOrganizationPatients";
import type { PatientsProgress } from "src-next/modules/clinical-insights/loadPhase";
import {
  getOrFetchInsightPatients,
  insightPatientCacheKey,
} from "src-next/modules/clinical-insights/services/insightPatientCache";
import { sharedMemoryCache } from "src-next/shared/cache/createMemoryCache";

function uniquePatientIds(rawIds: string[]): string[] {
  const ids = new Set<string>();
  for (const raw of rawIds) {
    const id = normalizePatientId(raw);
    if (!id || id === "unknown") continue;
    ids.add(id);
  }
  return Array.from(ids).sort();
}

function readCachedPatients(
  ids: string[],
  organizationPatients: ReadonlyMap<string, fhir5.Patient>,
): {
  map: Map<string, fhir5.Patient>;
  missing: string[];
} {
  const map = new Map<string, fhir5.Patient>();
  const missing: string[] = [];
  for (const id of ids) {
    const fromOrganization = organizationPatients.get(id);
    if (fromOrganization) {
      map.set(id, fromOrganization);
      continue;
    }
    const cached = sharedMemoryCache.get<fhir5.Patient>(insightPatientCacheKey(id));
    if (cached) map.set(id, cached.data);
    else missing.push(id);
  }
  return { map, missing };
}

/**
 * Hidrata Patient/{id} pela lista de atendidos da organização.
 * O GET unitário só começa depois que essa lista terminou de carregar,
 * e só para o id que não está nela (nem no consentimento já em memória).
 */
export function useInsightPatientHydration(rawIds: string[], enabled = true) {
  const { ready: organizationReady, patientsById } = useInsightOrganizationPatients();
  const ids = useMemo(() => (enabled ? uniquePatientIds(rawIds) : []), [rawIds, enabled]);
  const idsKey = ids.join("|");
  const idsRef = useRef(ids);
  idsRef.current = ids;

  const cached = useMemo(
    () => readCachedPatients(ids, patientsById),
    [idsKey, ids, patientsById],
  );
  const [networkMap, setNetworkMap] = useState<{
    key: string;
    map: Map<string, fhir5.Patient>;
  } | null>(null);
  const [progress, setProgress] = useState<PatientsProgress>({
    loaded: cached.map.size,
    total: ids.length,
  });

  useEffect(() => {
    const currentIds = idsRef.current;
    if (!enabled) {
      setNetworkMap(null);
      setProgress({ loaded: 0, total: 0 });
      return;
    }
    if (!organizationReady) {
      setNetworkMap(null);
      setProgress({ loaded: 0, total: currentIds.length });
      return;
    }

    const snapshot = readCachedPatients(currentIds, patientsById);
    if (snapshot.missing.length === 0) {
      setNetworkMap(null);
      setProgress({ loaded: currentIds.length, total: currentIds.length });
      return;
    }

    let cancelled = false;
    setNetworkMap(null);
    setProgress({ loaded: snapshot.map.size, total: currentIds.length });

    void getOrFetchInsightPatients(snapshot.missing, {
      organizationPatients: patientsById,
      shouldContinue: () => !cancelled,
      onProgress: (loaded) => {
        if (cancelled) return;
        setProgress({
          loaded: Math.min(currentIds.length, snapshot.map.size + loaded),
          total: currentIds.length,
        });
      },
    }).then((fetched) => {
      if (cancelled) return;
      const next = new Map(snapshot.map);
      for (const [id, patient] of fetched) next.set(id, patient);
      setNetworkMap({ key: idsKey, map: next });
      setProgress({ loaded: currentIds.length, total: currentIds.length });
    });

    return () => {
      cancelled = true;
    };
  }, [enabled, idsKey, organizationReady, patientsById]);

  const resolved = networkMap && networkMap.key === idsKey ? networkMap.map : null;
  const needsNetwork =
    enabled &&
    ids.length > 0 &&
    (!organizationReady || (cached.missing.length > 0 && resolved == null));
  const patientMap = resolved ?? cached.map;

  return {
    patientMap,
    patients: useMemo(() => Array.from(patientMap.values()), [patientMap]),
    isLoading: needsNetwork,
    progress: needsNetwork ? progress : { loaded: ids.length, total: ids.length },
  };
}
